// Client Journey — single guided progress page that strings the AI core (Sprints
// 2 → 5) into one tangible flow. Each step lights up as the client progresses.
//
// Steps:
//   1. Upload Credit Report          (Sprint 2 / 7B)
//   2. Review Dispute Candidates     (Sprint 2 output)
//   3. Letters Mailed                (Sprint 3 / 7C)
//   4. FCRA 30-day Response Window   (countdown)
//   5. CFPB / Arbitration Escalation (Sprints 4A / 4B)
//   6. Funding Match Results         (Sprint 5 / 7F)
import { useCallback, useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import { apiCall } from "../auth/AuthContext";
import {
  Upload, Search, Mail, Clock, Shield, Sparkles, Loader2, CheckCircle2, ArrowRight, Lock,
} from "lucide-react";

const DAY = 24 * 60 * 60 * 1000;

export default function ClientJourney() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [letters, setLetters] = useState([]);
  const [funding, setFunding] = useState([]);

  const fetchAll = useCallback(async () => {
    const [p, l, f] = await Promise.all([
      apiCall("/api/credit/profile").catch(() => ({ profile: null })),
      apiCall("/api/disputes/letters").catch(() => ({ records: [] })),
      apiCall("/api/funding/matches").catch(() => ({ records: [] })),
    ]);
    return {
      profile: p.profile || null,
      letters: l.records || [],
      funding: f.records || [],
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const d = await fetchAll();
      if (cancelled) return;
      setProfile(d.profile); setLetters(d.letters); setFunding(d.funding); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchAll]);

  // Derive step state
  const candidates = profile?.profile?.dispute_candidates || [];
  const sent = letters.filter((l) => l.status === "sent");
  const escalation = letters.filter((l) => l.recipient_type === "cfpb" || l.round_number === 3);
  const oldestSent = sent.reduce((acc, l) => {
    if (!l.sent_at) return acc;
    const d = new Date(l.sent_at);
    return !acc || d < acc ? d : acc;
  }, null);
  const daysLeft = oldestSent ? Math.max(0, 30 - Math.floor((Date.now() - oldestSent) / DAY)) : null;
  const fcraExpired = oldestSent && daysLeft === 0;

  const steps = [
    {
      key: "upload",
      label: "Share Your Report",
      icon: Upload,
      done: !!profile,
      cta: { to: "/dashboard/client/profile-insights", label: "Upload report" },
      desc: "Upload your most recent 3-bureau consumer report so we can see exactly where you stand.",
    },
    {
      key: "review",
      label: "Profile Analysis",
      icon: Search,
      done: candidates.length > 0,
      locked: !profile,
      desc: "We analyze your full financial profile and show you what underwriters and lenders see.",
      cta: candidates.length > 0
        ? { to: "/dashboard/client/profile-insights", label: "View analysis" }
        : null,
    },
    {
      key: "mailed",
      label: "Development Plan Prepared",
      icon: Mail,
      done: sent.length > 0,
      locked: candidates.length === 0,
      desc: "We build your personalized profile development plan and prepare any documentation for your authorization.",
      cta: letters.length > 0 ? { to: "/dashboard/client/records-review", label: "View tracker" } : null,
    },
    {
      key: "fcra",
      label: "Review Period",
      icon: Clock,
      done: fcraExpired,
      locked: sent.length === 0,
      desc: "Standard federal review timelines run their course while we track every response on your behalf.",
      cta: sent.length > 0 ? { to: "/dashboard/client/records-review", label: "Open tracker" } : null,
    },
    {
      key: "escalation",
      label: "Continued Advocacy",
      icon: Shield,
      done: escalation.length > 0,
      locked: !fcraExpired,
      desc: "If anything remains unresolved, we advance it through the appropriate channels on your behalf.",
    },
    {
      key: "funding",
      label: "Funding Readiness",
      icon: Sparkles,
      done: funding.length > 0,
      // Funding can run anytime — never lock it after profile exists
      locked: !profile,
      desc: "We match your strengthened profile to funding opportunities.",
      cta: { to: "/dashboard/client/funding-match", label: funding.length > 0 ? "View matches" : "Run match" },
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const pct = Math.round((completedCount / steps.length) * 100);

  return (
    <BrandShell dashboardChrome>
      <div className="bg-jc-black min-h-[calc(100vh-5rem)]" data-testid="client-journey">
        <div className="px-6 md:px-12 py-12 max-w-5xl mx-auto">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold mb-2">◆ Your Wealth Intelligence Journey</p>
        <h1 className="font-display text-jc-white" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)", lineHeight: 0.95 }}>
          Every step. Every milestone. One view.
        </h1>
        <p className="mt-4 text-jc-muted max-w-2xl">
          This is your guided path from profile analysis to funding readiness. Each step lights up as we move you through the process — so you always know exactly where you stand.
        </p>

        {/* Overall progress bar */}
        <div className="mt-8" data-testid="journey-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-gold">Progress</span>
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-jc-white">{completedCount} / {steps.length} · {pct}%</span>
          </div>
          <div className="h-1.5 bg-jc-gold/15 rounded">
            <div className="h-1.5 bg-jc-gold rounded transition-all duration-500" style={{ width: `${pct}%` }}/>
          </div>
        </div>

        {/* Steps */}
        {loading ? (
          <div className="mt-12 flex items-center justify-center text-jc-muted gap-3"><Loader2 size={18} className="animate-spin"/> Loading your journey…</div>
        ) : (
          <ol className="mt-10 space-y-4" data-testid="journey-steps">
            {steps.map((s, i) => <StepCard key={s.key} step={s} index={i + 1}/>)}
          </ol>
        )}
        </div>
      </div>
    </BrandShell>
  );
}

function StepCard({ step, index }) {
  const Icon = step.icon;
  const state = step.done ? "done" : step.locked ? "locked" : "active";
  const ring = state === "done" ? "border-jc-gold bg-jc-gold/5"
              : state === "active" ? "border-jc-white/40 bg-jc-navy/60"
              : "border-jc-muted/15 bg-jc-black/30 opacity-60";

  return (
    <li className={`border ${ring} p-5 md:p-6 rounded-xl transition-colors`} data-testid={`journey-step-${step.key}`}>
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center
          ${state === "done" ? "bg-jc-gold text-jc-black"
            : state === "active" ? "bg-jc-navy border border-jc-gold text-jc-gold"
            : "bg-jc-black border border-jc-muted/30 text-jc-muted"}`}>
          {state === "done" ? <CheckCircle2 size={22} strokeWidth={2}/>
            : state === "locked" ? <Lock size={18}/>
            : <Icon size={20} strokeWidth={1.75}/>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold mb-1">Step {index}</p>
          <h3 className="font-display text-lg md:text-xl text-jc-white">{step.label}</h3>
          <p className="mt-1.5 text-sm text-jc-muted">{step.desc}</p>
          {step.cta && state !== "locked" && (
            <a href={step.cta.to} data-testid={`journey-cta-${step.key}`}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.22em] text-jc-gold hover:translate-x-0.5 transition-transform">
              {step.cta.label} <ArrowRight size={12}/>
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
